/**
 * Host-side conversation success evaluation.
 *
 * For each Claude Code agent, ask the API which completed end-user
 * conversations still need scoring (GET /host/conversations/pending-evaluation),
 * reconstruct each conversation's turns from the agent's local session
 * transcripts, score it with a small evaluator model (a pluggable backend —
 * Claude Haiku via `claude -p` by default, or a fully-local OpenAI-compatible
 * model such as Gemma 4 on the host), and POST the verdict back
 * (POST /host/conversations/evaluation).
 *
 * SECURITY / DATA-MINIMISATION (the whole point of doing this host-side):
 *   The raw conversation transcript NEVER leaves the host. The control plane
 *   hands us only conversation_id + channel + channel_ref (no content); the
 *   manager reads the content from the on-disk transcripts, scores it locally
 *   via the agent's OWN Claude auth (so the content only reaches Anthropic, the
 *   exact boundary the agent already crosses), and POSTs back nothing but the
 *   low-sensitivity {score, verdict, summary}. The agt API/Postgres continues to
 *   see only counts/timestamps + this verdict for the conversations table.
 *
 * Same fire-and-forget contract as token-usage-monitor.ts: self-throttled,
 * self-bounded (caps spawns per cycle), errors swallowed — must never slow or
 * break the manager poll cycle. Claude Code framework only.
 */

import { readdirSync, readFileSync, statSync, type Dirent } from 'node:fs';
import { join } from 'node:path';
import {
  type EvalFailureReason,
  type EvalExclusionReason,
  type ConversationFailureCategory,
  isConversationFailureCategory,
  isSystemInjectedContent,
  isBareAck,
  buildFailureCategoryPromptLines,
  CONVERSATION_FAILURE_CATEGORIES,
} from '@augmented/core';
import { sessionTranscriptDir } from './daily-session.js';
import { getProjectDir } from './persistent-session.js';

// Re-evaluate at most this often per agent — conversations are low-volume and a
// completed one doesn't change, so there's no value polling more aggressively.
const MIN_CHECK_INTERVAL_MS = 5 * 60_000;
// Only read transcripts touched within this window. ENG-7137: widened 3d→7d to
// cut `not_reconstructable` coverage loss for agents that were dormant between a
// conversation and its evaluation: the persistent session file is appended
// continuously, so for an active agent its mtime stays fresh regardless; this
// only widens the backfill horizon for a transcript that has gone quiet.
const TRANSCRIPT_MTIME_WINDOW_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
// Pad the [started_at, last_message_at] window when selecting turns so a slightly
// skewed transcript timestamp still lands inside.
const WINDOW_PAD_MS = 5 * 60_000;
// Bound the prompt: per-turn and overall caps keep a long conversation from
// blowing up the eval call (and its cost / local-inference latency).
const MAX_TURN_CHARS = 1500;
const MAX_TRANSCRIPT_CHARS = 6000;

// Defaults for the built-in scoring backends; the kind is the
// 'conversation-eval-backend' flag, the rest are env tunables (see
// resolveConversationEvalBackend in manager-worker.ts).
//   claude-p      - Haiku via the `claude -p` subprocess. The default: runs
//                   under the agent's Max auth (effectively free) but is a heavy
//                   spawn. Same model id as packages/api/src/cron/memory-dreaming.ts.
//   anthropic-api - Haiku via a direct Anthropic Messages API fetch (no
//                   subprocess). Opt-in: metered per-token, needs an API key.
//   local         - an OpenAI-compatible endpoint (Ollama / vLLM / llama.cpp /
//                   LM Studio) serving e.g. Gemma 4 12B, keeping the transcript
//                   fully on the host (never reaches any external API).
export const DEFAULT_CLAUDE_EVAL_MODEL = 'claude-haiku-4-5-20251001';
export const DEFAULT_ANTHROPIC_MESSAGES_URL = 'https://api.anthropic.com/v1/messages';
const ANTHROPIC_API_VERSION = '2023-06-01';
export const DEFAULT_LOCAL_EVAL_URL = 'http://localhost:11434/v1/chat/completions';
export const DEFAULT_LOCAL_EVAL_MODEL = 'gemma4:12b';
const EVAL_TIMEOUT_MS = 120_000;

/** Minimal API surface — lets tests stub without the real transport. */
export interface ConversationEvaluatorApi {
  get<T = unknown>(path: string): Promise<T>;
  post<T = unknown>(path: string, body: unknown): Promise<T>;
}

/**
 * A pluggable scoring backend. `run` takes the eval prompt and returns the raw
 * model text; `model` is the id recorded in eval_model. The manager builds one
 * of these per its config (see selectEvalBackend) - `claude-p` (Haiku via the
 * `claude -p` subprocess, the default), `anthropic-api` (Haiku via a direct
 * Anthropic Messages API fetch), or `local` (an OpenAI-compatible endpoint such
 * as Ollama / vLLM serving e.g. Gemma 4 12B). The rest of this module is
 * backend-agnostic.
 *
 * The `claude-p` runner MUST run with the agent's auth applied and WITHOUT the
 * agent's CLAUDE.md / MCP servers — it's an objective evaluator, not the agent
 * persona (that runner lives in manager-worker.ts, which owns the spawn helpers
 * and stays transport-free for tests).
 */
export interface ConversationEvalBackend {
  readonly model: string;
  run(prompt: string): Promise<string>;
}

/**
 * Built-in `local` backend runner: POST the prompt to an OpenAI-compatible
 * chat-completions endpoint (Ollama, vLLM, llama.cpp server, LM Studio) and
 * return the assistant text. Keeps the transcript fully on the host — it never
 * reaches any external API, not even Anthropic. Pure (only needs fetch) so it's
 * unit-testable without a process.
 */
export async function runLocalEvalChat(
  prompt: string,
  opts: {
    url: string;
    model: string;
    apiKey?: string;
    timeoutMs?: number;
    /** Test seam. */
    fetchImpl?: typeof fetch;
  },
): Promise<string> {
  // Loopback-only: this backend's entire reason to exist is that the transcript
  // never leaves the host. Refuse any non-loopback target so a misconfigured
  // AGT_CONV_EVAL_LOCAL_URL can't silently ship transcripts off-box.
  const endpoint = new URL(opts.url);
  const hostname = endpoint.hostname.toLowerCase();
  const isLoopback =
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '::1' ||
    hostname === '[::1]';
  if (!isLoopback) {
    throw new Error(`local eval url must be loopback-only, got host '${endpoint.hostname}'`);
  }

  const doFetch = opts.fetchImpl ?? fetch;
  const res = await doFetch(opts.url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(opts.apiKey ? { Authorization: `Bearer ${opts.apiKey}` } : {}),
    },
    body: JSON.stringify({
      model: opts.model,
      messages: [{ role: 'user', content: prompt }],
      temperature: 0,
      max_tokens: 512,
      stream: false,
    }),
    signal: AbortSignal.timeout(opts.timeoutMs ?? EVAL_TIMEOUT_MS),
  });
  if (!res.ok) {
    throw new Error(`local eval endpoint ${opts.url} returned ${res.status}`);
  }
  const data = (await res.json()) as { choices?: Array<{ message?: { content?: string } }> };
  const text = data.choices?.[0]?.message?.content;
  if (typeof text !== 'string' || !text.trim()) {
    throw new Error('local eval endpoint returned no message content');
  }
  return text;
}

/**
 * Built-in `anthropic-api` backend runner (opt-in direct-API Claude path): POST
 * the eval prompt directly to the Anthropic Messages API and return the
 * assistant text. Replaces the heavier `claude -p` subprocess (full CLI spawn +
 * empty-MCP isolation dance + spawn-budget/reaper registration) with a single
 * fetch when selected.
 *
 * Privacy boundary is IDENTICAL to the old `claude -p` path: the transcript
 * reaches Anthropic (the exact boundary the agent itself already crosses) and
 * nothing else. The COST boundary differs - `claude -p` ran under the agent's
 * Max subscription (effectively free), whereas this is metered per-token against
 * the supplied API key. See resolveConversationEvalBackend / the operator env
 * docs for the rollout tradeoff.
 *
 * Pure (only needs fetch + an API key) so it's unit-testable without a process.
 */
export async function runAnthropicMessages(
  prompt: string,
  opts: {
    apiKey: string;
    model: string;
    maxTokens?: number;
    timeoutMs?: number;
    /** Override the endpoint (tests / proxies). Defaults to the public API. */
    url?: string;
    /** Test seam. */
    fetchImpl?: typeof fetch;
  },
): Promise<string> {
  if (!opts.apiKey) {
    throw new Error('anthropic-api eval requires an API key');
  }
  const doFetch = opts.fetchImpl ?? fetch;
  const res = await doFetch(opts.url ?? DEFAULT_ANTHROPIC_MESSAGES_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': opts.apiKey,
      'anthropic-version': ANTHROPIC_API_VERSION,
    },
    body: JSON.stringify({
      model: opts.model,
      max_tokens: opts.maxTokens ?? 512,
      temperature: 0,
      messages: [{ role: 'user', content: prompt }],
    }),
    signal: AbortSignal.timeout(opts.timeoutMs ?? EVAL_TIMEOUT_MS),
  });
  if (!res.ok) {
    throw new Error(`anthropic messages api returned ${res.status}`);
  }
  // The Messages API returns content as an array of typed blocks; concatenate
  // the text blocks (tool_use / thinking blocks, if any, are ignored).
  const data = (await res.json()) as { content?: Array<{ type?: string; text?: string }> };
  const text = (data.content ?? [])
    .filter((b) => b?.type === 'text' && typeof b.text === 'string')
    .map((b) => b.text as string)
    .join('')
    .trim();
  if (!text) {
    throw new Error('anthropic messages api returned no text content');
  }
  return text;
}

/** Backend kinds for conversation/memory evaluation. */
export type EvalBackendKind = 'anthropic-api' | 'claude-p' | 'local';

/**
 * ENG-7235: should the host-side conversation-eval + memory-extraction scorers
 * be suppressed for cost on this host?
 *
 * On an OpenRouter-metered host (claude_auth_mode='openrouter') every model call
 * is per-token spend, and these are idle, no-deliverable scorers - so we run
 * them only when the configured backend is `local` (a zero-cost, host-private
 * loopback model). The metered backends are suppressed: `anthropic-api` would
 * bill per token, and `claude-p` additionally can't authenticate on an
 * OpenRouter host (no Max OAuth) so it would retry-loop "Not logged in" every
 * cycle for nothing.
 *
 * Subscription / api_key hosts are never suppressed (the probe/eval is
 * effectively free under Max, and api_key hosts already chose a metered path
 * deliberately). A null mode (exchange not yet done) is treated conservatively
 * as "not metered" so eval keeps working until the host mode is positively
 * known to be OpenRouter.
 */
export function meteredScorerSuppressed(
  claudeAuthMode: 'subscription' | 'api_key' | 'openrouter' | null,
  backendKind: string,
): boolean {
  if (claudeAuthMode !== 'openrouter') return false;
  return (backendKind ?? '').trim().toLowerCase() !== 'local';
}

export interface SelectEvalBackendOptions {
  /** Raw AGT_CONV_EVAL_BACKEND value; unset/'' selects the default (anthropic-api). */
  kind: string;
  /** Haiku model id for the Claude paths (anthropic-api + claude-p). */
  claudeModel: string;
  /** Anthropic API key for the anthropic-api path (env-resolved + trimmed by the caller). */
  anthropicApiKey: string;
  /** Local-backend config. */
  local: { url: string; model: string; apiKey?: string };
  /**
   * Claude-p runner - supplied by the manager, which owns the spawn helpers and
   * stays transport-free for tests. Only invoked when kind === 'claude-p'.
   */
  claudePRunner: (prompt: string, model: string) => Promise<string>;
  /** Optional structured logger (manager.log). */
  log?: (msg: string) => void;
}

/**
 * Pure backend selector shared by conversation eval AND memory extraction (both
 * call manager-worker's resolveConversationEvalBackend, which delegates here).
 *
 * `kind` is normally the 'conversation-eval-backend' registry flag value the
 * manager resolves (env override, central flag, then compiled default
 * `claude-p`), so the FLEET default is governed by that flag. An unset/'' kind
 * here also resolves to `claude-p` (the safe, transcript-on-Max-auth default),
 * so the direct-API path is never reached by omission; `anthropic-api` and
 * `local` are selected explicitly. An unknown kind, or `anthropic-api` with no
 * API key, fails CLOSED: it returns a backend whose run() throws, so the
 * evaluator logs and retries later rather than silently falling back to a
 * different backend (which would quietly change the cost/auth boundary).
 */
export function selectEvalBackend(opts: SelectEvalBackendOptions): ConversationEvalBackend {
  const kind = (opts.kind ?? '').trim().toLowerCase();
  const log = opts.log ?? (() => {});

  if (kind === 'local') {
    const { url, model, apiKey } = opts.local;
    // Redact userinfo / query tokens before the URL reaches manager.log.
    const safeUrl = (() => {
      try {
        const parsed = new URL(url);
        return `${parsed.origin}${parsed.pathname}`;
      } catch {
        return '[invalid-url]';
      }
    })();
    log(`[conversation-eval] backend=local url=${safeUrl} model=${model}`);
    return { model, run: (prompt) => runLocalEvalChat(prompt, { url, model, apiKey }) };
  }

  if (kind === '' || kind === 'claude-p') {
    // Haiku via `claude -p` (heavy subprocess; the agent's Max auth). This is the
    // safe default for an unset/'' kind: it matches the registry flag default and
    // keeps the transcript on the agent's existing Max-auth boundary. Moving eval
    // onto the metered direct API must be an explicit `anthropic-api` selection,
    // never an accidental consequence of an omitted kind (privacy + cost boundary).
    log(`[conversation-eval] backend=claude-p model=${opts.claudeModel}`);
    return { model: opts.claudeModel, run: (prompt) => opts.claudePRunner(prompt, opts.claudeModel) };
  }

  if (kind === 'anthropic-api') {
    // Haiku via the Anthropic Messages API (direct fetch, no subprocess).
    if (!opts.anthropicApiKey) {
      // Fail closed - do NOT fall back to claude-p (different cost/auth boundary).
      log(
        '[conversation-eval] backend=anthropic-api but no API key (AGT_CONV_EVAL_ANTHROPIC_API_KEY / ANTHROPIC_API_KEY); evaluation disabled',
      );
      return {
        model: 'anthropic-api-missing-key',
        run: async () => {
          throw new Error(
            'anthropic-api eval requires AGT_CONV_EVAL_ANTHROPIC_API_KEY or ANTHROPIC_API_KEY',
          );
        },
      };
    }
    log(`[conversation-eval] backend=anthropic-api model=${opts.claudeModel}`);
    return {
      model: opts.claudeModel,
      run: (prompt) => runAnthropicMessages(prompt, { apiKey: opts.anthropicApiKey, model: opts.claudeModel }),
    };
  }

  // Fail closed: a typo'd backend must never silently move evaluation off its
  // intended boundary. The error surfaces per-run inside the evaluator's catch
  // (logged, retried later) without crashing the manager.
  log(
    `[conversation-eval] invalid AGT_CONV_EVAL_BACKEND='${kind}' - expected 'anthropic-api', 'claude-p', or 'local'; evaluation disabled`,
  );
  return {
    model: 'invalid-conversation-eval-backend',
    run: async () => {
      throw new Error(`Unsupported AGT_CONV_EVAL_BACKEND='${kind}'. Expected 'anthropic-api', 'claude-p', or 'local'.`);
    },
  };
}

interface PendingConversation {
  conversation_id: string;
  channel: string;
  channel_ref: string;
  started_at: string;
  last_message_at: string;
  message_count: number;
}

export interface Turn {
  role: 'user' | 'assistant';
  text: string;
  ts: number;
  /**
   * ENG-7137: for an assistant turn, the routing haystack - the channel/thread/
   * chat ids the reply targets, lifted verbatim from the reply tool's
   * `tool_use.input` (e.g. slack.reply `{channel, thread_ts, message_ts}`). Lets
   * a reply be attributed to its OWN conversation by id, immune to interleaving,
   * instead of positionally. Absent on plain-text replies (e.g. direct-chat).
   */
  refs?: string;
  /**
   * ENG-7137: the source transcript file (session) this turn came from. Used to
   * scope POSITIONAL attribution of a no-routing reply to its own session, so a
   * foreign routed reply interleaved from the pinned persistent session can't
   * steal/drop a direct-chat reply (direct-chat uses a fresh session per convo).
   */
  session?: string;
}

interface EvaluatorState {
  lastCheckedAt: number;
}

const state = new Map<string, EvaluatorState>();

/** Test helper — reset module state. Not for production use. */
export function _resetConversationEvaluatorState(): void {
  state.clear();
}

/**
 * Discriminator tokens a transcript user-turn must contain to belong to a
 * conversation. Derived from the channel_ref by dropping the channel prefix and
 * server-side literal markers (`dm`), leaving the real ids (slack channel/thread
 * or channel/user, telegram chat id, direct-chat session id) that appear
 * verbatim in the inbound `<channel …>` tag.
 */
export function channelRefTokens(channelRef: string): string[] {
  return channelRef
    .split(':')
    .slice(1) // drop the leading channel name (slack / telegram / direct-chat)
    .filter((p) => p && p !== 'dm');
}

/**
 * ENG-7137: the subset of channel_ref tokens that appear in an AGENT REPLY's
 * routing (the reply tool's `tool_use.input`), used to attribute assistant turns.
 *
 * This differs from {@link channelRefTokens} for DMs: a DM channel_ref carries
 * the participant user id (e.g. `slack:C123:dm:U456`), but the reply tool only
 * targets the channel (`{channel: 'C123', …}`); the user id never appears in the
 * reply input. So everything from the `dm` marker onward (the participant id) is
 * dropped here; the DM channel id alone uniquely identifies the conversation.
 * For threads (`slack:C123:111.1`) and telegram/direct-chat refs the tokens are
 * unchanged (channel + thread/message ts, which the reply input does echo).
 */
export function channelRefRoutingTokens(channelRef: string): string[] {
  const parts = channelRef.split(':').slice(1);
  const dmIdx = parts.indexOf('dm');
  const kept = dmIdx === -1 ? parts : parts.slice(0, dmIdx);
  return kept.filter((p) => p && p !== 'dm');
}

/** Coerce a transcript line's message content (string | block array) to text. */
function contentToText(content: unknown): string {
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) {
    return content
      .map((b) => {
        if (typeof b === 'string') return b;
        if (b && typeof b === 'object' && (b as { type?: string }).type === 'text') {
          return String((b as { text?: string }).text ?? '');
        }
        return '';
      })
      .filter(Boolean)
      .join(' ');
  }
  return '';
}

/** Best-effort JSON.stringify; never throws (transcript inputs are plain JSON). */
function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value) ?? '';
  } catch {
    return '';
  }
}

/**
 * ENG-7137: extract an assistant turn's scoreable text AND its reply-routing
 * haystack from a Claude Code message `content` (string | block array).
 *
 * Two things the old text-only path discarded and we now recover:
 *  - The user-facing reply TEXT for a channel-egress tool lives in the tool's
 *    `input.text` (e.g. slack.reply), NOT in a text block (which is often empty
 *    or a thin "On it…"). Scoring the text block alone under-credited real
 *    replies → spurious "partial". We concatenate text blocks + reply-tool text.
 *  - The reply's destination ids (channel/thread/chat) live in `tool_use.input`;
 *    we stringify the input (minus the prose `text` field) as the routing
 *    haystack so the reply can be attributed to its own conversation by id.
 */
export function extractAssistantContent(content: unknown): { text: string; refs: string } {
  if (typeof content === 'string') return { text: content.trim(), refs: '' };
  if (!Array.isArray(content)) return { text: '', refs: '' };
  const texts: string[] = [];
  const refParts: string[] = [];
  for (const b of content) {
    if (typeof b === 'string') {
      if (b.trim()) texts.push(b.trim());
      continue;
    }
    if (!b || typeof b !== 'object') continue;
    const block = b as { type?: string; text?: unknown; input?: unknown };
    if (block.type === 'text' && typeof block.text === 'string') {
      const t = block.text.trim();
      if (t) texts.push(t);
    } else if (block.type === 'tool_use' && block.input && typeof block.input === 'object') {
      // Routing haystack = the tool input MINUS the prose `text` field, so the
      // reply body can't contribute spurious token matches (every channel_ref
      // token must appear, so this is conservative anyway).
      const { text: replyText, ...routing } = block.input as Record<string, unknown>;
      refParts.push(safeStringify(routing));
      if (typeof replyText === 'string' && replyText.trim()) texts.push(replyText.trim());
    }
  }
  return { text: texts.join(' ').trim(), refs: refParts.join(' ').trim() };
}

/**
 * Parse user/assistant text turns out of a transcript JSONL. Defensive: unknown
 * line types, malformed JSON, and tool-only turns are skipped (mirrors
 * transcript-parser.ts's degrade-don't-throw contract). `session` (the source
 * file) is stamped on each turn for session-scoped positional attribution.
 */
export function parseTranscriptTurns(jsonl: string, session?: string): Turn[] {
  const turns: Turn[] = [];
  for (const line of jsonl.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    let obj: { type?: string; timestamp?: string; message?: { role?: string; content?: unknown } };
    try {
      obj = JSON.parse(trimmed);
    } catch {
      continue;
    }
    const role = obj.type === 'user' || obj.type === 'assistant' ? obj.type : undefined;
    if (!role) continue;
    const ts = obj.timestamp ? Date.parse(obj.timestamp) : NaN;
    // ENG-7137 (CodeRabbit): skip a turn with a missing/unparseable timestamp
    // rather than coercing it to 0. A ts of 0 is falsy, so reconstructConversation's
    // window guard (`if (turn.ts && …)`) would skip the bounds check for it,
    // making the turn eligible for EVERY conversation window and pulling stale or
    // malformed lines into unrelated reconstructions.
    if (!Number.isFinite(ts)) continue;
    if (role === 'assistant') {
      const { text, refs } = extractAssistantContent(obj.message?.content);
      if (!text) continue; // no scoreable reply (pure tool/react/thinking turn)
      turns.push({ role, text, ts, ...(refs ? { refs } : {}), ...(session ? { session } : {}) });
    } else {
      const text = contentToText(obj.message?.content).trim();
      if (!text) continue;
      turns.push({ role, text, ts, ...(session ? { session } : {}) });
    }
  }
  return turns;
}

/** Strip the inbound `<channel …>` wrapper tag so the prompt shows just the message. */
function stripChannelTag(text: string): string {
  // The tag is a single (possibly self-closing) element at the start of an
  // inbound user turn; remove the opening tag and any closing </channel>.
  return text
    .replace(/<channel\b[^>]*\/?>/i, '')
    .replace(/<\/channel>/i, '')
    .trim();
}

/**
 * Reconstruct a conversation's chronological user+assistant turns from the
 * agent's transcripts (ENG-7137 rework).
 *
 * A user turn enters the conversation when its text contains every channel_ref
 * token. Assistant turns are attributed by IDENTITY where possible:
 *  - A ROUTED reply (one carrying a `refs` routing haystack from its reply
 *    tool's input) belongs to this conversation iff its routing tokens all
 *    appear in `refs`, regardless of interleaving. This fixes the busy
 *    multi-thread "agent went quiet" bug: when the agent receives a second
 *    thread's message before replying to the first, the first reply is no longer
 *    dropped just because a foreign user turn intervened.
 *  - A PLAIN-TEXT reply (no `refs`, e.g. direct-chat, which uses a fresh session
 *    per conversation) is attributed POSITIONALLY but scoped to its own source
 *    session, so a foreign routed reply interleaved from the pinned persistent
 *    session never steals or drops it.
 *
 * The old single global `inConversation` flag could not distinguish these and
 * dropped/mis-paired replies on interleaved sessions.
 */
export function reconstructConversation(
  allTurns: Turn[],
  channelRef: string,
  windowStartMs: number,
  windowEndMs: number,
): Turn[] {
  const userTokens = channelRefTokens(channelRef);
  if (userTokens.length === 0) return [];
  const routingTokens = channelRefRoutingTokens(channelRef);
  const sorted = [...allTurns].sort((a, b) => a.ts - b.ts);
  const out: Turn[] = [];
  // A conversation must be anchored by ITS user turn: without the user's ask the
  // transcript is not meaningfully scoreable ("did the agent help?" needs the
  // question), and an unanchored routed reply should stay not_reconstructable
  // rather than become a mis-scored assistant-only transcript (CodeRabbit).
  let hasMatchedUser = false;
  // The session of the currently-open THIS-conversation user turn, for scoping
  // positional (no-routing) reply attribution. `undefined` is a valid session
  // key (turns parsed without a source file), distinct from `null` (closed).
  let openUserSession: string | null | undefined = null;
  const hasAll = (haystack: string, toks: string[]) =>
    toks.length > 0 && toks.every((t) => haystack.includes(t));
  for (const turn of sorted) {
    if (turn.ts && (turn.ts < windowStartMs || turn.ts > windowEndMs)) continue;
    if (turn.role === 'user') {
      if (hasAll(turn.text, userTokens)) {
        hasMatchedUser = true;
        openUserSession = turn.session;
        out.push({ ...turn, text: stripChannelTag(turn.text) });
      } else if (turn.session === openUserSession) {
        // A foreign user turn in OUR open session closes the positional window;
        // a foreign user in a different session leaves ours intact.
        openUserSession = null;
      }
    } else if (turn.refs) {
      // Routed reply: attribute by its own destination ids, interleave-immune,
      // but only once this conversation has been anchored by its user turn.
      if (hasMatchedUser && hasAll(turn.refs, routingTokens)) out.push(turn);
    } else if (turn.session === openUserSession) {
      // Plain-text reply with no routing: positional within our own session.
      out.push(turn);
    }
  }
  // No user turn ever matched → leave it not_reconstructable, not assistant-only.
  return hasMatchedUser ? out : [];
}

/**
 * ENG-7137: should this reconstructed conversation be EXCLUDED from scoring
 * entirely (a terminal, one-shot drop rather than a verdict)? Returns the
 * exclusion reason, or null to score normally.
 *
 *  - synthetic_probe: a synthetic liveness probe that leaked past ingest
 *    sender_class classification: any user turn carries the probe marker.
 *  - ack_only: the agent's only turn(s) are bare "ack" acknowledgements (e.g. a
 *    probe ack), so there is no real exchange to grade. Requires at least one
 *    assistant turn and that EVERY assistant turn be a bare ack (a substantive
 *    reply anywhere disqualifies the exclusion).
 */
export function conversationExclusionReason(turns: Turn[]): EvalExclusionReason | null {
  if (turns.some((t) => t.role === 'user' && isSystemInjectedContent(t.text))) {
    return 'synthetic_probe';
  }
  const assistant = turns.filter((t) => t.role === 'assistant');
  if (assistant.length > 0 && assistant.every((t) => isBareAck(t.text))) {
    return 'ack_only';
  }
  return null;
}

/** Render the reconstructed turns into a bounded transcript for the evaluator. */
export function renderTranscript(turns: Turn[]): string {
  const lines: string[] = [];
  let total = 0;
  for (const turn of turns) {
    const label = turn.role === 'user' ? 'User' : 'Agent';
    const body = turn.text.length > MAX_TURN_CHARS ? `${turn.text.slice(0, MAX_TURN_CHARS)}…` : turn.text;
    const line = `${label}: ${body}`;
    if (total + line.length > MAX_TRANSCRIPT_CHARS) break;
    lines.push(line);
    total += line.length;
  }
  return lines.join('\n\n');
}

function buildEvalPrompt(channel: string, transcript: string): string {
  return `You are a strict QA evaluator scoring whether an AI agent successfully helped an end-user in a single ${channel} conversation. Judge ONLY the agent's effectiveness for the user — not tone, length, or policy.

Conversation transcript:
"""
${transcript}
"""

Score the agent's success:
- score: integer 0-100 (0 = ignored/unhelpful/wrong, 100 = fully resolved the user's need)
- verdict: "success" (need clearly met), "partial" (some help but genuinely incomplete), or "failure" (did not help / made it worse)
- summary: ONE short sentence (max 140 chars) explaining the score. Do NOT quote sensitive user content.

How to choose the verdict (read carefully — most conversations are short and one-sided):
- A complete, correct, self-contained answer to the user's question is a "success" EVEN IF the user never replied to confirm. End-users rarely send a closing "thanks". Do NOT downgrade a good answer to "partial" merely because the transcript ends after the agent's reply or because there is no explicit confirmation. Absence of confirmation is NOT ambiguity.
- Use "partial" ONLY when the agent's own response is genuinely incomplete: it asked the user a clarifying question that was never answered, gave a hedged/half answer, promised a follow-up that is not present, or addressed only part of a multi-part request.
- Use "failure" when the agent ignored the user, was wrong, or made things worse.
- If the transcript looks truncated or cut off mid-exchange (a reconstruction artifact, not the agent's fault), judge only what the agent actually delivered; do not invent incompleteness from a missing tail.
- failure_category: ONLY when verdict is "failure", the single best-fitting reason from this exact set: ${buildFailureCategoryPromptLines()}. Use null when verdict is "success" or "partial".

Respond with ONLY a JSON object, no other text:
{"score": 0-100, "verdict": "success|partial|failure", "summary": "...", "failure_category": "${CONVERSATION_FAILURE_CATEGORIES.join('|')} or null"}`;
}

interface ParsedVerdict {
  score: number;
  verdict: 'success' | 'partial' | 'failure';
  summary: string;
  /** ENG-6915: closed-set outcome reason; only meaningful (non-null) on a failure verdict. */
  failureCategory: ConversationFailureCategory | null;
}

/** Parse the Haiku JSON verdict; returns null on any malformed/out-of-range output. */
export function parseVerdict(raw: string): ParsedVerdict | null {
  // Tolerate markdown code fences and surrounding prose — extract the first
  // {...} block.
  const match = raw.match(/\{[\s\S]*\}/);
  if (!match) return null;
  let obj: { score?: unknown; verdict?: unknown; summary?: unknown; failure_category?: unknown };
  try {
    obj = JSON.parse(match[0]);
  } catch {
    return null;
  }
  const score = typeof obj.score === 'number' ? obj.score : Number(obj.score);
  if (!Number.isFinite(score) || score < 0 || score > 100) return null;
  if (obj.verdict !== 'success' && obj.verdict !== 'partial' && obj.verdict !== 'failure') return null;
  const summary = typeof obj.summary === 'string' ? obj.summary.slice(0, 140) : '';
  // A category is only meaningful on a failure verdict; ignore it (and any
  // unknown value) otherwise. The DB CHECK enforces the same invariant.
  const failureCategory =
    obj.verdict === 'failure' && isConversationFailureCategory(obj.failure_category)
      ? obj.failure_category
      : null;
  return { score: Math.round(score), verdict: obj.verdict, summary, failureCategory };
}

/**
 * For one agent: pull pending conversations, score each from local transcripts,
 * and report verdicts. Self-throttled and bounded; all faults are swallowed.
 */
export async function maybeEvaluateConversations(args: {
  api: ConversationEvaluatorApi;
  backend: ConversationEvalBackend;
  codeName: string;
  agentId: string;
  log: (msg: string) => void;
  now?: Date;
  /** Test seam: override the transcript directory. */
  transcriptDir?: string;
}): Promise<void> {
  const { api, backend, codeName, agentId, log } = args;
  const now = args.now ?? new Date();
  const nowMs = now.getTime();

  const existing = state.get(codeName);
  if (existing && nowMs - existing.lastCheckedAt < MIN_CHECK_INTERVAL_MS) {
    return;
  }
  state.set(codeName, { lastCheckedAt: nowMs });

  let pending: PendingConversation[];
  try {
    const resp = await api.get<{ ok?: boolean; conversations?: PendingConversation[] }>(
      `/host/conversations/pending-evaluation?agent_id=${encodeURIComponent(agentId)}`,
    );
    pending = resp?.conversations ?? [];
  } catch (err) {
    // The eval pipeline can't even start: surface it on the host so a down /
    // unreachable control plane is visible, not just logged.
    const msg = toHealthErrorMessage(err);
    log(`[conversation-eval] ${codeName}: pending fetch failed: ${msg}`);
    await reportBackendHealth(api, log, codeName, { ok: false, error: `pending fetch: ${msg}` });
    return;
  }
  if (pending.length === 0) return;

  // Read recent transcripts once, then score each conversation against them.
  const dir = args.transcriptDir ?? sessionTranscriptDir(getProjectDir(codeName));
  const allTurns = readRecentTurns(dir, nowMs);
  if (allTurns.length === 0) {
    // No local transcript content at all, bump attempts (bounded retry) and
    // record the reason so the give-up is explainable on the reporting surface.
    for (const conv of pending) {
      await reportSkip(api, agentId, conv.conversation_id, 'no_transcript', log, codeName);
    }
    return;
  }

  // Per-run eval-backend health: the silently-broken-backend gap lives here.
  // We only learn anything when a conversation actually reaches the backend, so
  // health is reported once at the end, and only if the backend was exercised.
  let backendSucceeded = false;
  let backendError: string | null = null;

  for (const conv of pending) {
    const windowStart = Date.parse(conv.started_at) - WINDOW_PAD_MS;
    const windowEnd = Date.parse(conv.last_message_at) + WINDOW_PAD_MS;
    const turns = reconstructConversation(allTurns, conv.channel_ref, windowStart, windowEnd);
    if (turns.length === 0) {
      await reportSkip(api, agentId, conv.conversation_id, 'not_reconstructable', log, codeName);
      continue;
    }

    // ENG-7137: terminal exclusions. A synthetic probe that leaked past ingest
    // classification, or an ack-only exchange, should never be scored. Report a
    // terminal skip (the API jumps attempts to the cap) so it drops from pending
    // in one shot and stays out of the scored denominator.
    const exclusion = conversationExclusionReason(turns);
    if (exclusion) {
      await reportSkip(api, agentId, conv.conversation_id, exclusion, log, codeName);
      continue;
    }

    // ENG-7137 instrumentation (AC#1): a conversation that reconstructs to user
    // turn(s) with ZERO attributed agent replies is the "agent went quiet" shape
    // that the strict grader scores partial/failure. Most of these are a
    // reconstruction artifact (a reply that exists but couldn't be attributed),
    // not a genuine non-reply — logging it makes the residual rate greppable so
    // the before/after impact of the attribution fix is measurable on a host.
    if (!turns.some((t) => t.role === 'assistant')) {
      log(
        `[conversation-eval] ${codeName}: ${conv.conversation_id.slice(0, 8)} reconstructed user-only (${turns.length} turn(s), 0 agent replies)`,
      );
    }

    const transcript = renderTranscript(turns);
    if (!transcript.trim()) {
      await reportSkip(api, agentId, conv.conversation_id, 'empty_transcript', log, codeName);
      continue;
    }

    // Scorer failures are treated as transient (endpoint down, model hiccup,
    // garbled output): log and retry on a later cycle WITHOUT reportSkip —
    // eval_attempts is the bounded-retry budget for permanently-
    // unreconstructable conversations, and a reconstructable one must not have
    // its evaluation permanently suppressed by a few backend hiccups. They DO
    // count against host-grain backend health below.
    let verdict: ParsedVerdict | null;
    try {
      const out = await backend.run(buildEvalPrompt(conv.channel, transcript));
      verdict = parseVerdict(out);
    } catch (err) {
      backendError = toHealthErrorMessage(err);
      log(`[conversation-eval] ${codeName}: scoring failed: ${backendError}`);
      continue;
    }
    if (!verdict) {
      // Output that doesn't parse is a backend-health signal too (a misconfigured
      // / wrong model can return prose every time), so it feeds the run health.
      // Last failure this run wins, consistent with the catch block above.
      backendError = 'unparseable verdict';
      log(`[conversation-eval] ${codeName}: unparseable verdict for ${conv.conversation_id.slice(0, 8)}`);
      continue;
    }

    // A parseable verdict means the backend is healthy this run.
    backendSucceeded = true;

    try {
      await api.post('/host/conversations/evaluation', {
        agent_id: agentId,
        conversation_id: conv.conversation_id,
        score: verdict.score,
        verdict: verdict.verdict,
        summary: verdict.summary,
        failure_category: verdict.failureCategory,
        model: backend.model,
      });
      log(
        `[conversation-eval] ${codeName}: ${conv.conversation_id.slice(0, 8)} → ${verdict.verdict} (${verdict.score})`,
      );
    } catch (err) {
      log(`[conversation-eval] ${codeName}: report failed: ${(err as Error).message}`);
    }
  }

  // Report host-grain backend health once per run. A single success clears the
  // host error state; otherwise the last backend error (if the backend was
  // exercised at all) is recorded so a silently-broken backend climbs the
  // consecutive-failure counter. Runs that never reached the backend (all
  // data-shaped skips) leave health untouched: they say nothing about it.
  if (backendSucceeded) {
    await reportBackendHealth(api, log, codeName, { ok: true });
  } else if (backendError) {
    await reportBackendHealth(api, log, codeName, { ok: false, error: backendError, model: backend.model });
  }
}

async function reportSkip(
  api: ConversationEvaluatorApi,
  agentId: string,
  conversationId: string,
  reason: EvalFailureReason | EvalExclusionReason,
  log: (msg: string) => void,
  codeName: string,
): Promise<void> {
  try {
    await api.post('/host/conversations/evaluation', {
      agent_id: agentId,
      conversation_id: conversationId,
      skipped: true,
      // The data-shaped reason this conversation couldn't be scored. The API
      // stores it alongside the eval_attempts bump so the eventual give-up is
      // explainable on the reporting surface.
      failure_reason: reason,
    });
  } catch (err) {
    log(`[conversation-eval] ${codeName}: skip report failed: ${(err as Error).message}`);
  }
}

/**
 * Sanitise a backend error before it leaves the host on the health signal. The
 * raw message can echo the local/proxy eval URL with userinfo or a query token
 * (and is persisted on hosts.eval_backend_last_error + shown on debug surfaces),
 * so strip bearer tokens / key-bearing query params / URL userinfo and bound the
 * length. Best-effort defence-in-depth, mirroring runLocalEvalChat's URL redact.
 */
export function toHealthErrorMessage(err: unknown): string {
  const raw =
    err instanceof Error ? err.message : typeof err === 'string' ? err : 'unknown backend error';
  return raw
    .replace(/\s+/g, ' ')
    .replace(/(Bearer\s+)[^\s]+/gi, '$1[redacted]')
    .replace(/([?&](?:api[_-]?key|token|secret|key)=)[^&\s]+/gi, '$1[redacted]')
    .replace(/:\/\/[^/\s]+@/g, '://[redacted]@')
    .trim()
    .slice(0, 300);
}

/**
 * Report this run's host-grain eval-backend health (ENG-6661). A success clears
 * the host error state; a failure records the message and climbs the host's
 * consecutive-failure counter, making a silently-broken / misconfigured eval
 * backend visible fleet-wide instead of only in manager.log. Best-effort and
 * non-throwing, like the rest of this module.
 */
async function reportBackendHealth(
  api: ConversationEvaluatorApi,
  log: (msg: string) => void,
  codeName: string,
  health: { ok: boolean; error?: string; model?: string },
): Promise<void> {
  try {
    await api.post('/host/conversations/eval-backend-health', {
      ok: health.ok,
      error: health.error,
      model: health.model,
    });
  } catch (err) {
    log(`[conversation-eval] ${codeName}: backend-health report failed: ${(err as Error).message}`);
  }
}

/**
 * Enumerate recent transcript files under `dir` and parse all their turns.
 *
 * ENG-7137: recurses into subdirectories (e.g. `<sessionId>/subagents/`) which
 * the old non-recursive readdir missed (a coverage gap when a conversation was
 * handled in a background sub-task). Symlinked dirs are NOT followed (withFileTypes
 * reports a symlink as a symlink, not a directory), so there's no loop risk. Each
 * turn is stamped with its source file name as the session key.
 */
export function readRecentTurns(dir: string, nowMs: number): Turn[] {
  const turns: Turn[] = [];
  const visit = (d: string): void => {
    let entries: Dirent[];
    try {
      entries = readdirSync(d, { withFileTypes: true });
    } catch {
      return;
    }
    for (const ent of entries) {
      const full = join(d, ent.name);
      if (ent.isDirectory()) {
        visit(full);
        continue;
      }
      if (!ent.isFile() || !ent.name.endsWith('.jsonl')) continue;
      let mtimeMs: number;
      try {
        mtimeMs = statSync(full).mtimeMs;
      } catch {
        continue;
      }
      if (nowMs - mtimeMs > TRANSCRIPT_MTIME_WINDOW_MS) continue;
      let content: string;
      try {
        content = readFileSync(full, 'utf8');
      } catch {
        continue;
      }
      turns.push(...parseTranscriptTurns(content, ent.name));
    }
  };
  visit(dir);
  return turns;
}
